import { faCog, faLayerPlus } from "@fortawesome/pro-regular-svg-icons";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { UpsertAgentModal } from "src/agents/UpsertAgentModal";
import { useAgent } from "src/agents/queries/management";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { DashboardContent } from "src/layout/DashboardContent";
import { NavItem, NavSidebar } from "src/layout/NavSideBar";
import { PageHeader } from "src/layout/PageHeader";
import { HashKeys } from "src/router/SearchParams";
import { appendCurrentFlags } from "src/router/featureFlags";
import {
  AgentVersionsPageParams,
  getUrlToAgentsPage,
  getUrlToAgentVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentPage: React.FC = () => {
  const { orgId, wsId, agentId } = useParamsDecode<AgentVersionsPageParams>();
  const { data: agent, isLoading } = useAgent(agentId);
  const agentsPageUrl = getUrlToAgentsPage({ orgId, wsId });
  const location = useLocation();
  const navigate = useNavigate();

  const isEditModalOpen = location.hash === HashKeys.Edit;

  const handleCloseModal = () => {
    navigate(appendCurrentFlags(location.pathname));
  };

  return (
    <>
      <DashboardContent
        Header={
          <PageHeader
            breadcrumbs={[
              {
                key: "agents-page",
                breadcrumb: (
                  <PageHeader.Breadcrumb
                    dataLoc="agents-page"
                    href={agentsPageUrl}
                    label="Agents"
                    hideIcon
                  />
                ),
              },
              {
                key: "agent-versions",
                breadcrumb: isLoading ? (
                  <SkeletonPlaceholder height="h-4" width="w-24" />
                ) : (
                  <PageHeader.Breadcrumb
                    dataLoc="agent-versions"
                    href={agentsPageUrl}
                    label={agent?.name ?? "Unable to load agent"}
                    active
                  />
                ),
              },
            ]}
          />
        }
        sidebar={<AgentNavSidebar />}
        removePadding
      >
        <Outlet />
      </DashboardContent>
      <UpsertAgentModal
        agent={agent}
        open={isEditModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

const AgentNavSidebar = () => {
  const { agentId, orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();
  const [collapsed, setCollapsed] = useLocalStorage(
    "agentDetailNavigationCollapsed",
    false,
  );

  const currentPageUrl = useLocation();

  return (
    <div className="border-l border-gray-100">
      <NavSidebar collapsed={collapsed} setCollapsed={setCollapsed}>
        <NavItem
          dataLoc="versions"
          icon={faLayerPlus}
          title="Versions"
          to={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
        />
        <NavItem
          dataLoc="agent-settings"
          icon={faCog}
          title="Settings"
          to={appendCurrentFlags(currentPageUrl.pathname) + HashKeys.Edit}
          neverShowActive
        />
      </NavSidebar>
    </div>
  );
};
